import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle, ArrowLeft } from "lucide-react";
import { getPaymentStatus } from "../lib/api";

const MAX_ATTEMPTS = 8;

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [info, setInfo] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    let timer;
    const poll = async () => {
      if (!sessionId) {
        setState("error");
        return;
      }
      try {
        const data = await getPaymentStatus(sessionId);
        setInfo(data);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          return;
        }
        attempts.current += 1;
        if (attempts.current >= MAX_ATTEMPTS) {
          setState("pending");
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch (e) {
        attempts.current += 1;
        if (attempts.current >= MAX_ATTEMPTS) {
          setState("error");
          return;
        }
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#08080a] flex items-center justify-center px-5">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="glass rounded-3xl p-10 md:p-14 max-w-md w-full text-center border border-white/8"
      >
        {state === "checking" && (
          <>
            <Loader2 size={56} className="text-[#ff6a00] mx-auto animate-spin" />
            <h1 className="font-display font-bold text-2xl text-white mt-6">Vérification du paiement…</h1>
            <p className="text-white/55 mt-3">Merci de patienter quelques secondes.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 size={64} className="text-green-400 mx-auto" />
            <h1 className="font-display font-extrabold text-3xl text-white mt-6">Paiement confirmé !</h1>
            <p className="text-white/60 mt-3">
              Merci pour votre confiance. Votre formation est validée
              {info?.amount ? ` (${info.amount} ${(info.currency || "eur").toUpperCase()})` : ""}.
              Vous recevrez les accès par e-mail très prochainement.
            </p>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 size={56} className="text-[#ff6a00] mx-auto" />
            <h1 className="font-display font-bold text-2xl text-white mt-6">Paiement en cours de traitement</h1>
            <p className="text-white/55 mt-3">
              Votre paiement est en cours de validation. Vous recevrez une confirmation par e-mail.
            </p>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle size={64} className="text-red-400 mx-auto" />
            <h1 className="font-display font-bold text-2xl text-white mt-6">Un problème est survenu</h1>
            <p className="text-white/55 mt-3">
              Nous n'avons pas pu confirmer votre paiement. Si vous avez été débité, contactez-moi.
            </p>
          </>
        )}
        <Link to="/" className="btn-ghost mt-8 inline-flex justify-center">
          <ArrowLeft size={16} /> Retour à l'accueil
        </Link>
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;
